import { Box, IconButton, Stack, Typography, styled } from '@mui/material'
import React, { ReactNode, useCallback } from 'react'
import { commify } from 'ethers/lib/utils'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import Link from 'next/link'
import SwapLinkButton from './SwapLinkButton'
import { ExistingCrossChainAmount } from './ExistingCrossChainAmount'
import { SupportedTicker, SwapInfo, TICKERS_MAP_BY_NAME } from '@/constants/token'
import { RowCenterY } from '@/components/common/Rows'
import TokenLogo from '@/components/common/TokenLogo'
import { FlexCol } from '@/components/common/Columns'
import { shouldUseValutContract } from '@/utils'
import { CHAIN_INFO, UNISAT_BRC20_URL } from '@/constants'
import useDepositAddressByBtc from '@/hooks/useDepositAddressByBtc'
import { shortenAddress, shortenBtcAddress } from '@/utils/address'
import { VAULT_ADDRESS } from '@/constants/contract/contractAddress'

const Wrapper = styled(Box)`
  border: 1px solid rgba(191, 255, 11, 0.2);
  background: #111303;
  padding: 20px;
`

const Text1 = styled(Typography)`
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 140%;
`

const Text2 = styled(Typography)`
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
`

const Text2Opacity = styled(Text2)`
  opacity: 0.4;
` as typeof Typography

interface MobileDashboardCardProps {
  logoSrc: string
  tickerName: SupportedTicker
  price?: any
  renderSwap?: () => ReactNode
  totalCrossChainVolume?: number
  allOverCrossChainVolume: string
  swapInfo?: SwapInfo[]
}

const MobileDashboardCard = ({
  logoSrc,
  tickerName,
  price,
  totalCrossChainVolume,
  swapInfo,
}: MobileDashboardCardProps) => {
  const { data: depositAddress } = useDepositAddressByBtc()

  const renderProtogenAndTargetChain = useCallback(() => {
    if (shouldUseValutContract(tickerName)) {
      return (
        <Text2>
          {TICKERS_MAP_BY_NAME[tickerName].supportChains.map((id) => CHAIN_INFO[id].abbreviation).join(';')}/
          <Text2Opacity component="span">Bitcoin</Text2Opacity>
        </Text2>
      )
    } else
      return (
        <Text2>
          Bitcoin/
          <Text2Opacity component="span">
            {TICKERS_MAP_BY_NAME[tickerName].supportChains.map((id) => CHAIN_INFO[id].abbreviation).join(';')}
          </Text2Opacity>
        </Text2>
      )
  }, [tickerName])

  const formatedVolumn = totalCrossChainVolume ? commify(totalCrossChainVolume.toFixed(2)) : '-'

  return (
    <Wrapper>
      <Stack spacing={20}>
        <RowCenterY>
          <TokenLogo src={logoSrc} size={24} alt={tickerName} />
          <Text1 sx={{ ml: 8 }}>{tickerName}</Text1>
          <Text1 sx={{ ml: 'auto' }}>{price ? `$ ${commify(Number(price).toFixed(8))}` : '-'}</Text1>

          {!!swapInfo && (
            <RowCenterY sx={{ columnGap: 4 }}>
              {swapInfo?.map((swap) => (
                <SwapLinkButton key={swap.url} swap={swap.name} href={swap.url} />
              ))}
            </RowCenterY>
          )}
        </RowCenterY>

        <FlexCol sx={{ gap: 8 }}>
          <Text2Opacity>Protogen chain/Target chain</Text2Opacity>
          {renderProtogenAndTargetChain()}
        </FlexCol>

        <FlexCol sx={{ gap: 8 }}>
          <Text2Opacity>Total Cross Chain Volume</Text2Opacity>
          <RowCenterY sx={{ gap: 4 }}>
            <Text2>{formatedVolumn}</Text2>
            {price && typeof totalCrossChainVolume === 'number' ? (
              <Text2Opacity component="span" sx={{ whiteSpace: 'nowrap' }}>
                {`$ ${commify((totalCrossChainVolume * price).toFixed(2))}`}
              </Text2Opacity>
            ) : null}
          </RowCenterY>
        </FlexCol>

        <FlexCol sx={{ gap: 8 }}>
          <Text2Opacity>Existing cross-chain amount</Text2Opacity>
          <ExistingCrossChainAmount tickerName={tickerName} />
        </FlexCol>

        <RowCenterY>
          <Text2Opacity>Attestation Address</Text2Opacity>

          <Text1 sx={{ textDecorationLine: 'underline', ml: 'auto' }}>
            {shouldUseValutContract(tickerName)
              ? shortenAddress(VAULT_ADDRESS[TICKERS_MAP_BY_NAME[tickerName].supportChains[0]])
              : depositAddress
              ? shortenBtcAddress(depositAddress)
              : '-'}
          </Text1>
          <Link
            href={
              shouldUseValutContract(tickerName)
                ? `${CHAIN_INFO[TICKERS_MAP_BY_NAME[tickerName].supportChains[0]].blockExplorers.default.url}/address/${
                    VAULT_ADDRESS[TICKERS_MAP_BY_NAME[tickerName].supportChains[0]]
                  }`
                : UNISAT_BRC20_URL + depositAddress
            }
            target="_blank"
          >
            <IconButton size="small" sx={{ ml: 4 }}>
              <OpenInNewIcon sx={{ width: 20, height: 20 }} />
            </IconButton>
          </Link>
        </RowCenterY>
      </Stack>
    </Wrapper>
  )
}

export default MobileDashboardCard
